<script setup lang="ts">
/**
 * HelpDeskDialog
 *
 * A reusable "Submit Help Desk Ticket" modal that can be embedded in any
 * application (tms-app-soho, tms-app-product, etc.).
 *
 * Supports two modes controlled by the `isLoggedIn` prop:
 *
 * **Logged-in mode** (default):
 *   Name (auto-populated, read-only) · Current Path (auto-populated, read-only)
 *   Issue Type · Subject · Description · Attach Screenshot
 *
 * **Not-logged-in mode** (`isLoggedIn="false"`):
 *   Name (editable, mandatory) · Email ID (mandatory, validated) · Issue Type
 *   Subject · Description · Attach Screenshot
 *
 * Props
 * ─────
 * visible       – v-model boolean that controls dialog open/close state
 * isLoggedIn    – whether the current user is authenticated (default true)
 * userName      – logged-in user's full name (auto-populated, read-only)
 * userEmail     – user's email address (required; read-only when logged in)
 * currentPath   – current route path (auto-populated, read-only)
 *
 * Events
 * ──────
 * update:visible – standard v-model close event
 * submit         – emitted with HelpDeskTicketPayload when the form is valid
 * cancel         – emitted when the user closes without submitting
 */
import { reactive, ref, computed, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import IconComponent from '../icons/IconComponent.vue';
import { useToaster } from '../../composables/useToaster';
import { useSharedTranslation } from '../../composables/useTranslationProvider';

// ─── Types ────────────────────────────────────────────────────────────────────

export type IssueTypeOption = {
  label: string;
  value: string;
};

export type HelpDeskTicketPayload = {
  /** User's name (auto-populated when logged in, user-entered otherwise) */
  userName: string;
  /** Route path where ticket was initiated (logged-in mode only) */
  currentPath: string;
  /** Email address (always required — auto-populated when logged in, user-entered otherwise) */
  email: string;
  /** Selected issue type value */
  issueType: string;
  /** One-line subject */
  subject: string;
  /** Detailed description */
  description: string;
  /** Optional attached file */
  attachment: File | null;
};

const { t, locale } = useSharedTranslation();

// ─── Props / Emits ────────────────────────────────────────────────────────────

const props = withDefaults(
  defineProps<{
    visible: boolean;
    /**
     * Whether the current user is authenticated.
     * Controls form layout: logged-in shows read-only Name + Current Path;
     * not-logged-in shows editable Name + mandatory Email ID.
     */
    isLoggedIn?: boolean;
    userName?: string;
    userEmail?: string;
    currentPath?: string;
    /** Override the default issue-type options */
    issueTypeOptions?: IssueTypeOption[];
    /** Whether the submit button should show a loading spinner */
    loading?: boolean;
    /**
     * When true (default) the dialog automatically resets and closes after
     * emitting `submit`. Set to false when the parent manages the async
     * submit lifecycle (e.g. via useHelpDesk) so the dialog only closes
     * after the parent confirms success.
     */
    autoCloseOnSubmit?: boolean;
    /**
     * When true (default) a success toast is shown after auto-close.
     * Has no effect when autoCloseOnSubmit is false.
     */
    showSuccessToast?: boolean;
    /**
     * Optional hospital contact details to display above the ticket form.
     * Only non-null fields are rendered.
     */
    hospitalContact?: {
      facilityName?: string | null;
      contactPerson?: string | null;
      contactPhone?: string | null;
      contactEmail?: string | null;
    } | null;
  }>(),
  {
    isLoggedIn: true,
    userName: '',
    userEmail: '',
    currentPath: '',
    loading: false,
    autoCloseOnSubmit: true,
    showSuccessToast: true,
    hospitalContact: null,
  },
);

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'submit', payload: HelpDeskTicketPayload): void;
  (e: 'cancel'): void;
}>();

// Falls back to translated default issue-type options when the caller
// doesn't override the `issueTypeOptions` prop (kept out of withDefaults()
// since defineProps() defaults cannot reference the local `t` function).
const resolvedIssueTypeOptions = computed<IssueTypeOption[]>(() => {
  const _lng = locale.value; // reactivity trigger so labels re-translate on locale change
  return props.issueTypeOptions ?? [
    { label: t('helpDesk.issueTypeBug', 'Bug / Error'), value: 'BUG' },
    { label: t('helpDesk.issueTypeAccess', 'Access / Permissions'), value: 'ACCESS_ISSUE' },
    { label: t('helpDesk.issueTypeFeature', 'Feature Request'), value: 'FEATURE_REQUEST' },
    { label: t('helpDesk.issueTypeData', 'Data Issue'), value: 'DATA_ISSUE' },
    { label: t('common.other', 'Other'), value: 'OTHER' },
  ];
});

// ─── Form State ───────────────────────────────────────────────────────────────

const form = reactive({
  /** Editable name field (non-logged-in mode only) */
  name: '',
  /** Email address (non-logged-in mode only, mandatory) */
  email: '',
  issueType: null as IssueTypeOption | null,
  subject: '',
  description: '',
});

const attachment = ref<File | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const isDraggingOver = ref(false);

// ─── Validation ───────────────────────────────────────────────────────────────

const errors = reactive({
  name: '',
  email: '',
  issueType: '',
  subject: '',
  description: '',
  attachment: '',
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function clearErrors() {
  errors.name = '';
  errors.email = '';
  errors.issueType = '';
  errors.subject = '';
  errors.description = '';
  errors.attachment = '';
}

function validate(): boolean {
  clearErrors();
  let valid = true;

  // Name is always required — auto-populated from the prop when logged in,
  // but must be entered by hand (and validated) in anonymous mode.
  if (!props.isLoggedIn && !form.name.trim()) {
    errors.name = t('helpDesk.nameRequired', 'Name is required.');
    valid = false;
  }

  // Email is always required — sourced from the prop (logged-in) or the form (anonymous)
  const emailValue = props.isLoggedIn ? props.userEmail.trim() : form.email.trim();
  if (!emailValue) {
    errors.email = t('helpDesk.emailRequired', 'Email address is required.');
    valid = false;
  } else if (!EMAIL_RE.test(emailValue)) {
    errors.email = t('helpDesk.emailInvalid', 'Please enter a valid email address.');
    valid = false;
  }

  if (!form.issueType) {
    errors.issueType = t('helpDesk.issueTypeRequired', 'Issue type is required.');
    valid = false;
  }
  if (!form.subject.trim()) {
    errors.subject = t('helpDesk.subjectRequired', 'Subject is required.');
    valid = false;
  } else if (form.subject.trim().length > 150) {
    errors.subject = t('helpDesk.subjectTooLong', 'Subject must be 150 characters or fewer.');
    valid = false;
  }
  if (!form.description.trim()) {
    errors.description = t('helpDesk.descriptionRequired', 'Description is required.');
    valid = false;
  }

  return valid;
}

// ─── File handling ────────────────────────────────────────────────────────────

const ALLOWED_TYPES = ['image/png', 'image/jpeg'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

function processFile(file: File) {
  errors.attachment = '';

  if (!ALLOWED_TYPES.includes(file.type)) {
    errors.attachment = t('helpDesk.attachmentInvalidType', 'Only PNG, JPG, or JPEG files are allowed.');
    return;
  }
  if (file.size > MAX_FILE_SIZE) {
    errors.attachment = t('helpDesk.attachmentTooLarge', 'File size must not exceed 10 MB.');
    return;
  }

  attachment.value = file;
}

function onFileInputChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) processFile(file);
  // Reset so the same file can be re-selected after removal
  target.value = '';
}

function onDropZoneClick() {
  fileInputRef.value?.click();
}

function onDragOver(event: DragEvent) {
  event.preventDefault();
  isDraggingOver.value = true;
}

function onDragLeave(event: DragEvent) {
  if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node)) {
    isDraggingOver.value = false;
  }
}

function onDrop(event: DragEvent) {
  event.preventDefault();
  isDraggingOver.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) processFile(file);
}

function removeAttachment() {
  attachment.value = null;
  errors.attachment = '';
}

const attachmentSizeLabel = computed(() => {
  if (!attachment.value) return '';
  const kb = attachment.value.size / 1024;
  return kb >= 1024
    ? `${(kb / 1024).toFixed(1)} MB`
    : `${kb.toFixed(0)} KB`;
});

// ─── Submit / Cancel ──────────────────────────────────────────────────────────

const toast = useToaster();

function resetForm() {
  form.name = '';
  form.email = '';
  form.issueType = null;
  form.subject = '';
  form.description = '';
  attachment.value = null;
  clearErrors();
}

function handleCancel() {
  if (props.loading) return;
  resetForm();
  emit('cancel');
  emit('update:visible', false);
}

// Reset form whenever the dialog closes (visible: true → false).
// This covers the case where the parent closes the dialog externally
// (e.g. useHelpDesk after a successful async submit) without calling
// handleCancel(), which would otherwise leave stale state for the next open.
watch(
  () => props.visible,
  (isVisible, wasVisible) => {
    if (wasVisible && !isVisible) {
      resetForm();
    }
  },
);

function handleSubmit() {
  if (!validate()) return;

  const payload: HelpDeskTicketPayload = {
    userName: props.isLoggedIn ? props.userName : form.name.trim(),
    currentPath: props.isLoggedIn ? props.currentPath : '',
    email: props.isLoggedIn ? props.userEmail : form.email.trim(),
    issueType: form.issueType?.value ?? '',
    subject: form.subject.trim(),
    description: form.description.trim(),
    attachment: attachment.value,
  };

  emit('submit', payload);

  // Only auto-close + toast when the consumer has not opted in to managing
  // the async submit lifecycle externally (e.g. via useHelpDesk).
  // Using autoCloseOnSubmit avoids the race condition where props.loading
  // is still false at the moment this runs (parent hasn't processed the
  // emit yet).
  if (props.autoCloseOnSubmit) {
    if (props.showSuccessToast) {
      toast.success(t('helpDesk.submitSuccess', 'Your help desk ticket has been submitted successfully.'));
    }
    resetForm();
    emit('update:visible', false);
  }
}

// ─── Dialog PT (passthrough) ──────────────────────────────────────────────────

const dialogPT = computed(() => ({
  root: {
    class: 'bg-white rounded-xl shadow-2xl p-0 border border-gray-200 w-full flex flex-col',
    style: 'max-width: 34rem; width: 100%;',
  },
  header: { class: 'shrink-0 !p-0' },
  content: { class: 'text-gray-700' },
  footer: { class: 'shrink-0 !p-0' },
}));
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :draggable="false"
    :closable="false"
    :pt="dialogPT"
    position="top"
    @update:visible="(v) => { if (!v) handleCancel(); }"
  >
    <!-- ── Header ──────────────────────────────────────────────────────── -->
    <template #header>
      <div class="w-full border-b border-gray-200 flex items-center justify-between px-6 py-3">
        <span class="text-base font-semibold text-gray-900 uppercase tracking-wide">
          {{ t('helpDesk.title', 'Submit Help Desk Ticket') }}
        </span>
        <Button
          text
          rounded
          severity="secondary"
          class="!p-2"
          :aria-label="t('common.close', 'Close')"
          :disabled="loading"
          @click="handleCancel"
        >
          <template #icon>
            <IconComponent
              name="Close"
              size-class="h-5 w-5"
              color="text-gray-600"
            />
          </template>
        </Button>
      </div>
    </template>

    <!-- ── Body ───────────────────────────────────────────────────────── -->
    <div class="px-6 py-5 flex flex-col gap-4">
      <!-- Hospital Contact Details (shown above the form when provided) -->
      <div
        v-if="hospitalContact && (hospitalContact.facilityName || hospitalContact.contactPerson || hospitalContact.contactPhone || hospitalContact.contactEmail)"
        class="rounded-lg border border-blue-100 bg-blue-50 p-4 flex flex-col gap-2"
      >
        <p class="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">
          {{ t('helpDesk.hospitalContact', 'Hospital Contact') }}
        </p>
        <div
          v-if="hospitalContact.facilityName"
          class="flex items-center gap-2 text-sm text-blue-800"
        >
          <IconComponent
            name="Hospital"
            size-class="h-4 w-4 shrink-0"
            color="text-blue-500"
            variant="stroke"
          />
          <span>{{ hospitalContact.facilityName }}</span>
        </div>
        <div
          v-if="hospitalContact.contactPerson"
          class="flex items-center gap-2 text-sm text-blue-800"
        >
          <IconComponent
            name="PersonOutline"
            size-class="h-4 w-4 shrink-0"
            color="text-blue-500"
          />
          <span>{{ hospitalContact.contactPerson }}</span>
        </div>
        <div
          v-if="hospitalContact.contactPhone"
          class="flex items-center gap-2 text-sm text-blue-800"
        >
          <IconComponent
            name="Phone"
            size-class="h-4 w-4 shrink-0"
            color="text-blue-500"
          />
          <span>{{ hospitalContact.contactPhone }}</span>
        </div>
        <div
          v-if="hospitalContact.contactEmail"
          class="flex items-center gap-2 text-sm text-blue-800"
        >
          <IconComponent
            name="Mail"
            size-class="h-4 w-4 shrink-0"
            color="text-blue-500"
          />
          <span>{{ hospitalContact.contactEmail }}</span>
        </div>
      </div>

      <!-- Name -->
      <div class="flex flex-col gap-1">
        <label
          for="hd-name"
          class="text-sm font-medium text-gray-700"
        >
          {{ t('common.name', 'Name') }}
          <span class="text-red-500 ml-0.5">*</span>
        </label>
        <InputText
          v-if="isLoggedIn"
          id="hd-name"
          :model-value="userName"
          disabled
          class="w-full !bg-gray-50 !text-gray-500 !cursor-default"
        />
        <InputText
          v-else
          id="hd-name"
          v-model="form.name"
          :placeholder="t('helpDesk.namePlaceholder', 'Enter your name')"
          class="w-full"
          :class="{ 'p-invalid': errors.name }"
        />
        <p
          v-if="errors.name"
          class="text-xs text-red-500 mt-0.5"
        >
          {{ errors.name }}
        </p>
      </div>

      <!-- Email ID (always visible; disabled when logged in) -->
      <div class="flex flex-col gap-1">
        <label
          for="hd-email"
          class="text-sm font-medium text-gray-700"
        >
          {{ t('helpDesk.emailLabel', 'Email ID') }}
          <span class="text-red-500 ml-0.5">*</span>
        </label>
        <InputText
          v-if="isLoggedIn"
          id="hd-email"
          :model-value="userEmail"
          disabled
          class="w-full !bg-gray-50 !text-gray-500 !cursor-default"
        />
        <InputText
          v-else
          id="hd-email"
          v-model="form.email"
          type="email"
          :placeholder="t('helpDesk.emailPlaceholder', 'Enter email ID')"
          class="w-full"
          :class="{ 'p-invalid': errors.email }"
        />
        <p
          v-if="errors.email"
          class="text-xs text-red-500 mt-0.5"
        >
          {{ errors.email }}
        </p>
      </div>

      <!-- Current Path (logged-in only, read-only) -->
      <div
        v-if="isLoggedIn"
        class="flex flex-col gap-1"
      >
        <label
          for="hd-current-path"
          class="text-sm font-medium text-gray-700"
        >{{ t('helpDesk.currentPathLabel', 'Current Path') }}</label>
        <InputText
          id="hd-current-path"
          :model-value="currentPath"
          disabled
          class="w-full !bg-gray-50 !text-gray-500 !cursor-default"
        />
      </div>

      <!-- Issue Type (required dropdown) -->
      <div class="flex flex-col gap-1">
        <label
          for="hd-issue-type"
          class="text-sm font-medium text-gray-700"
        >
          {{ t('helpDesk.issueTypeLabel', 'Issue Type') }}
          <span class="text-red-500 ml-0.5">*</span>
        </label>
        <Select
          v-model="form.issueType"
          input-id="hd-issue-type"
          :options="resolvedIssueTypeOptions"
          option-label="label"
          :placeholder="t('helpDesk.issueTypePlaceholder', 'Select issue type')"
          class="w-full"
          :class="{ 'p-invalid': errors.issueType }"
        />
        <p
          v-if="errors.issueType"
          class="text-xs text-red-500 mt-0.5"
        >
          {{ errors.issueType }}
        </p>
      </div>

      <!-- Subject (required) -->
      <div class="flex flex-col gap-1">
        <label
          for="hd-subject"
          class="text-sm font-medium text-gray-700"
        >
          {{ t('helpDesk.subjectLabel', 'Subject') }}
          <span class="text-red-500 ml-0.5">*</span>
        </label>
        <InputText
          id="hd-subject"
          v-model="form.subject"
          :placeholder="t('helpDesk.subjectPlaceholder', 'Brief description of the issue')"
          class="w-full"
          :class="{ 'p-invalid': errors.subject }"
        />
        <p
          v-if="errors.subject"
          class="text-xs text-red-500 mt-0.5"
        >
          {{ errors.subject }}
        </p>
      </div>

      <!-- Description (required) -->
      <div class="flex flex-col gap-1">
        <label
          for="hd-description"
          class="text-sm font-medium text-gray-700"
        >
          {{ t('ui.overview.description', 'Description') }}
          <span class="text-red-500 ml-0.5">*</span>
        </label>
        <Textarea
          id="hd-description"
          v-model="form.description"
          rows="4"
          :placeholder="t('helpDesk.descriptionPlaceholder', 'Please provide detailed information about the issue you\'re experiencing...')"
          class="w-full resize-none"
          :class="{ 'p-invalid': errors.description }"
        />
        <p
          v-if="errors.description"
          class="text-xs text-red-500 mt-0.5"
        >
          {{ errors.description }}
        </p>
      </div>

      <!-- Attach Screenshot (optional) -->
      <div class="flex flex-col gap-1">
        <label
          for="hd-attachment"
          class="text-sm font-medium text-gray-700"
        >{{ t('helpDesk.attachmentLabel', 'Attach Screenshot') }}</label>

        <!-- File already selected -->
        <div
          v-if="attachment"
          class="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
        >
          <div class="flex items-center gap-2 min-w-0">
            <IconComponent
              name="Attachment"
              size-class="h-5 w-5 shrink-0"
              color="text-gray-500"
            />
            <span class="text-sm text-gray-700 truncate">{{ attachment.name }}</span>
            <span class="text-xs text-gray-400 shrink-0">{{ attachmentSizeLabel }}</span>
          </div>
          <Button
            text
            rounded
            severity="secondary"
            class="!p-1.5 shrink-0"
            :aria-label="t('helpDesk.removeAttachment', 'Remove attachment')"
            @click="removeAttachment"
          >
            <template #icon>
              <IconComponent
                name="Close"
                size-class="h-4 w-4"
                color="text-gray-600"
              />
            </template>
          </Button>
        </div>

        <!-- Drop zone -->
        <div
          v-else
          role="button"
          tabindex="0"
          :aria-label="t('helpDesk.uploadScreenshot', 'Upload screenshot')"
          :class="[
            'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 cursor-pointer transition-colors select-none',
            isDraggingOver
              ? 'border-primary-400 bg-primary-50'
              : 'border-gray-300 bg-white hover:border-primary-400 hover:bg-primary-50',
          ]"
          @click="onDropZoneClick"
          @keydown.enter.prevent="onDropZoneClick"
          @keydown.space.prevent="onDropZoneClick"
          @dragover="onDragOver"
          @dragleave="onDragLeave($event)"
          @drop="onDrop"
        >
          <!-- Upload icon -->
          <IconComponent
            name="CloudUpload"
            size-class="h-8 w-8"
            color="text-gray-400"
            variant="stroke"
          />
          <p class="text-sm text-gray-600">
            <span class="font-medium text-primary-600 pr-1">{{ t('helpDesk.clickToUpload', 'Click to upload') }}</span>
            <span> {{ t('helpDesk.orDragAndDrop', 'or drag and drop') }}</span>
          </p>
          <p class="text-xs text-gray-400 -mt-1">
            {{ t('helpDesk.attachmentHint', 'PNG, JPG or JPEG (MAX. 10MB)') }}
          </p>
        </div>

        <!-- Hidden file input -->
        <input
          id="hd-attachment"
          ref="fileInputRef"
          type="file"
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          class="sr-only"
          tabindex="-1"
          @change="onFileInputChange"
        >

        <p
          v-if="errors.attachment"
          class="text-xs text-red-500 mt-0.5"
        >
          {{ errors.attachment }}
        </p>
      </div>
    </div>

    <!-- ── Footer ─────────────────────────────────────────────────────── -->
    <template #footer>
      <div class="w-full border-t border-gray-200 px-6 py-4 flex items-center gap-3">
        <Button
          :label="t('helpDesk.submitTicket', 'Submit Ticket')"
          severity="primary"
          :loading="loading"
          :disabled="loading"
          @click="handleSubmit"
        />
        <Button
          :label="t('common.cancel', 'Cancel')"
          severity="secondary"
          outlined
          :disabled="loading"
          @click="handleCancel"
        />
      </div>
    </template>
  </Dialog>
</template>
